package co.uk.paxes.pinpoint.web.platform

import co.uk.paxes.pinpoint.web.platform.IPlayerIdentity
import co.uk.paxes.pinpoint.web.platform.ILeaderboardService
import co.uk.paxes.pinpoint.web.platform.IMultiplayerService
import co.uk.paxes.pinpoint.web.platform.IAdService

/**
 * Environment detection and platform service management.
 * Detects whether the game is running in Facebook Instant Games or standalone mode.
 */
object PlatformEnvironment {
    
    enum class Environment {
        FACEBOOK,
        DISCORD,
        PLAYGAMA,
        STANDALONE
    }
    
    private var _environment: Environment? = null
    private var _playerIdentity: IPlayerIdentity? = null
    private var _leaderboardService: ILeaderboardService? = null
    private var _multiplayerService: IMultiplayerService? = null
    private var _adService: IAdService? = null
    
    /**
     * Detect the runtime environment by checking for FBInstant global object.
     * Must be called before any platform services are accessed.
     */
    fun detectEnvironment(): Environment {
        if (_environment == null) {
            // Check for PlayGama - use hostname detection (most reliable)
            // PlayGama games run in iframe on playgama.com or partner domains
            val hostname = js("window.location.hostname") as String
            val isInIframe = js("window.self !== window.top") as Boolean
            val hasBridge = js("typeof window.bridge !== 'undefined'") as Boolean
            
            // PlayGama detection: bridge exists AND (in iframe OR on playgama domain)
            val isPlayGama = hasBridge && (
                isInIframe || 
                hostname.contains("playgama") ||
                hostname.contains("play.gama")
            )
            
            // Check for FBInstant global object AND verify we're actually in Facebook context
            val hasFBInstant = js("typeof FBInstant !== 'undefined'") as Boolean
            val isInFacebookFrame = hasFBInstant && isInIframe
            val isFacebook = isInFacebookFrame
            
            // Check for Discord (Discord runs in iframe with frame_id query param)
            val isDiscord = js("window.location.search.indexOf('frame_id') !== -1") as Boolean
            
            _environment = when {
                isPlayGama -> Environment.PLAYGAMA
                isFacebook -> Environment.FACEBOOK
                isDiscord -> Environment.DISCORD
                else -> Environment.STANDALONE
            }
            console.log("🌍 Environment detected: ${_environment}")
            console.log("🌍 Detection details: hasBridge=$hasBridge, isInIframe=$isInIframe, hostname=$hostname, isPlayGama=$isPlayGama")
        }
        return _environment!!
    }
    
    /**
     * Get the current environment. Throws if detectEnvironment() hasn't been called.
     */
    fun getEnvironment(): Environment {
        return _environment ?: throw IllegalStateException("Environment not detected. Call detectEnvironment() first.")
    }
    
    /**
     * Check if running in Facebook mode.
     */
    fun isFacebook(): Boolean {
        return getEnvironment() == Environment.FACEBOOK
    }
    
    /**
     * Check if running in Discord mode.
     */
    fun isDiscord(): Boolean {
        return getEnvironment() == Environment.DISCORD
    }

    /**
     * Check if running in PlayGama mode.
     */
    fun isPlayGama(): Boolean {
        return getEnvironment() == Environment.PLAYGAMA
    }

    /**
     * Check if running in standalone mode.
     */
    fun isStandalone(): Boolean {
        return getEnvironment() == Environment.STANDALONE
    }
    
    /**
     * Initialize platform services based on detected environment.
     * Must be called after detectEnvironment().
     */
    fun initializePlatformServices(
        playerIdentity: IPlayerIdentity,
        leaderboardService: ILeaderboardService,
        multiplayerService: IMultiplayerService,
        adService: IAdService
    ) {
        _playerIdentity = playerIdentity
        _leaderboardService = leaderboardService
        _multiplayerService = multiplayerService
        _adService = adService
        console.log("✅ Platform services initialized for ${getEnvironment()} mode")
    }
    
    /**
     * Get the player identity service.
     */
    fun getPlayerIdentity(): IPlayerIdentity {
        return _playerIdentity ?: throw IllegalStateException("Platform services not initialized")
    }
    
    /**
     * Get the leaderboard service.
     */
    fun getLeaderboardService(): ILeaderboardService {
        return _leaderboardService ?: throw IllegalStateException("Platform services not initialized")
    }
    
    /**
     * Get the multiplayer service.
     */
    fun getMultiplayerService(): IMultiplayerService {
        return _multiplayerService ?: throw IllegalStateException("Platform services not initialized")
    }
    
    /**
     * Get the ad service.
     */
    fun getAdService(): IAdService {
        return _adService ?: throw IllegalStateException("Platform services not initialized")
    }
}
